import {
  findByPropertyRecursive,
  type PropertyNames,
} from '@principle-theorem/shared';
import { sortBy, uniqBy } from 'lodash';
import { FeatureVisibility, type IFeature } from './feature';
import { IPermission } from './permission';
import { PermissionDisplay, PermissionGroup } from './permission-group';

export class FeaturesCollection {
  features: IFeature[] = [];

  constructor(
    private _originalFeatures: IFeature[] = [],
    private _isProduction: boolean
  ) {
    this.features = getAllFeatures(this._originalFeatures);
  }

  byVisibility(...visibility: FeatureVisibility[]): IFeature[] {
    return this.features.filter((feature: IFeature) =>
      visibility.includes(feature.visibility)
    );
  }

  byOptInFeatures(workspaceOptInFeatures: string[]): IFeature[] {
    if (!workspaceOptInFeatures.length) {
      return [];
    }

    const optInFeatures = this.features.filter(
      (feature) => feature.visibility === FeatureVisibility.OptIn
    );

    return optInFeatures.filter((feature) =>
      workspaceOptInFeatures.includes(feature.label)
    );
  }

  toArray(): IFeature[] {
    return this.features;
  }

  findByLabel(
    featureLabel: string,
    features?: IFeature[]
  ): IFeature | undefined {
    if (!features) {
      features = this.features;
    }
    return findByPropertyRecursive<IFeature, PropertyNames<IFeature>>(
      'label',
      featureLabel,
      features,
      'features'
    );
  }

  isFeatureEnabled(
    feature: IFeature,
    workspaceOptInFeatures: string[] = []
  ): boolean {
    return isFeatureEnabled(
      feature,
      this,
      this._isProduction,
      workspaceOptInFeatures
    );
  }

  isFeatureEnabledByLabel(
    label: string,
    workspaceOptInFeatures: string[] = []
  ): boolean {
    const feature = this.findByLabel(label);
    return feature
      ? this.isFeatureEnabled(feature, workspaceOptInFeatures)
      : false;
  }

  getFeaturePermissions(workspaceOptInFeatures: string[]): PermissionGroup[] {
    const groups = this._originalFeatures
      .map((feature) => ({
        label: feature.label,
        permissions: this.getEnabledPermissions(
          [feature],
          workspaceOptInFeatures
        ),
      }))
      .filter((feature) => feature.permissions.length > 0)
      .map((feature) => {
        const permissions = feature.permissions.map(
          (permission) =>
            new PermissionDisplay(permission.description, permission.value)
        );

        return new PermissionGroup(feature.label, permissions);
      });

    return sortBy(groups, 'name');
  }

  getEnabledPermissions(
    features: IFeature[],
    workspaceOptInFeatures: string[]
  ): IPermission[] {
    if (!features.length) {
      return [];
    }
    const permissions = features
      .filter((feature) =>
        this.isFeatureEnabled(feature, workspaceOptInFeatures)
      )
      .map((feature) => {
        return [
          ...feature.permissions,
          ...this.getEnabledPermissions(
            feature.features ?? [],
            workspaceOptInFeatures
          ),
        ];
      })
      .flat();

    return uniqBy(permissions, 'value');
  }
}

const devFeatureVisibility: FeatureVisibility[] = [
  FeatureVisibility.Dev,
  FeatureVisibility.Alpha,
  FeatureVisibility.Beta,
  FeatureVisibility.Stable,
];

const prodFeatureVisibility: FeatureVisibility[] = [
  FeatureVisibility.Alpha,
  FeatureVisibility.Beta,
  FeatureVisibility.Stable,
];

export function isFeatureEnabled(
  feature: IFeature,
  collection: FeaturesCollection,
  isProduction: boolean,
  workspaceOptInFeatures: string[]
): boolean {
  const filteredFeatures = !isProduction
    ? collection.byVisibility(...devFeatureVisibility)
    : collection.byVisibility(...prodFeatureVisibility);

  const optInFeatures = collection.byOptInFeatures(workspaceOptInFeatures);
  const combinedFeatures = [...filteredFeatures, ...optInFeatures];

  return (
    !!combinedFeatures.length &&
    combinedFeatures.some((enabledFeature) => enabledFeature === feature)
  );
}

export function getAllFeatures(features: IFeature[]): IFeature[] {
  if (!features.length) {
    return [];
  }
  return features
    .map((feature) => {
      return [feature, ...getAllFeatures(feature.features ?? [])];
    })
    .flat();
}

export function getAllPermissions(features: IFeature[]): IPermission[] {
  if (!features.length) {
    return [];
  }
  return features
    .map((feature) => {
      return [
        ...feature.permissions,
        ...getAllPermissions(feature.features ?? []),
      ];
    })
    .flat();
}
